import { createContext, ReactNode, useContext, useState } from 'react';
import { ChildProfile } from '../models/childProfile';
import { Post } from '../models/post';
import { UserProfile } from '../models/userProfile';
import { Organisation } from '../models/organisation';
import { OrgConfig } from '../models/orgConfig';
import { Entitlements, ENTITLEMENTS_DEFAULT } from '../models/entitlements';
import { StaffProfile } from '../models/staffProfile';
import { decodeActiveUserToken } from '../utils/staffUtils';

/** Mapping of role name → role ID, loaded from the API at startup */
export type RolesMap = Record<string, number>;

export type AppState = {
  user: UserProfile | null;
  userRoleIds: number[];
  roles: RolesMap;
  childProfiles: ChildProfile[];
  selectedChildProfile: ChildProfile | null;
  posts: Post[];
  selectedOrganisation: Organisation | null;
  /** Per-org feature config — schedule grid + attendance defaults + report
   *  Setting label. Hydrated alongside selectedOrganisation. Null until
   *  loaded; consumers should fall back to safe defaults while waiting. */
  orgConfig: OrgConfig | null;
  /** What the current org's plan includes. Hydrated alongside orgConfig.
   *  Fails CLOSED while loading — see ENTITLEMENTS_DEFAULT. Consumers use it
   *  to decide what to render; the API is what actually enforces it. */
  entitlements: Entitlements;
  /** PIN-authenticated active user for this session. Cleared on org switch or logout. */
  activeUser: StaffProfile | null;
  /** Short-lived JWT for the active user. Stored in sessionStorage for axios. */
  activeUserToken: string | null;
  /** Idle lock timeout in milliseconds. Fetched from org settings; defaults to 300000ms (5 min) until loaded. */
  idleTimeoutMs: number;
  /** True while the device is locked (idle-timeout OR manual "Lock device" from the menu). The
   *  IdleLockGuard owns the PinKeypad overlay; it watches this flag instead of its own state. */
  isLocked: boolean;
  setUser: (user: UserProfile | null) => void;
  setUserRoleIds: (groups: number[]) => void;
  setRoles: (roles: RolesMap) => void;
  setChildProfiles: (children: ChildProfile[]) => void;
  setSelectedChildProfile: (child: ChildProfile | null) => void;
  setPosts: (posts: Post[]) => void;
  setSelectedOrganisation: (org: Organisation | null) => void;
  setOrgConfig: (config: OrgConfig | null) => void;
  setEntitlements: (e: Entitlements) => void;
  setActiveUser: (user: StaffProfile | null) => void;
  setActiveUserToken: (token: string | null) => void;
  setIdleTimeoutMs: (ms: number) => void;
  setIsLocked: (locked: boolean) => void;
};

const AppStateContext = createContext<AppState | undefined>(undefined);

export const AppStateProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [userRoleIds, setUserRoleIds] = useState<number[]>([]);
  const [roles, setRoles] = useState<RolesMap>({});
  const [childProfiles, setChildProfiles] = useState<ChildProfile[]>([]);
  const [selectedChildProfile, setSelectedChildProfile] = useState<ChildProfile | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | null>(null);
  const [orgConfig, setOrgConfig] = useState<OrgConfig | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlements>(ENTITLEMENTS_DEFAULT);
  const [activeUser, setActiveUser] = useState<StaffProfile | null>(
    () => decodeActiveUserToken(sessionStorage.getItem('activeUserToken'))
  );
  const [activeUserToken, setActiveUserToken] = useState<string | null>(
    // Only hydrate a token that's still valid — an expired one must not be
    // treated as an authenticated session (the API silently ignores it, so
    // requests would 403 rather than 401). Keeps this in step with activeUser.
    () => {
      const t = sessionStorage.getItem('activeUserToken');
      return decodeActiveUserToken(t) ? t : null;
    }
  );
  const [idleTimeoutMs, setIdleTimeoutMs] = useState<number>(300_000);
  // The lock flag is persisted to sessionStorage so a page refresh can't shake
  // off the PIN lock: without this, `isLocked` reset to false on F5 and exposed
  // the app underneath the keypad. Hydrated on load; every setIsLocked keeps the
  // flag in sync so both idle-timeout and manual "Lock device" survive a refresh.
  const [isLocked, setIsLockedState] = useState<boolean>(
    () => sessionStorage.getItem('deviceLocked') === '1'
  );
  const setIsLocked = (locked: boolean) => {
    if (locked) sessionStorage.setItem('deviceLocked', '1');
    else sessionStorage.removeItem('deviceLocked');
    setIsLockedState(locked);
  };

  return (
    <AppStateContext.Provider value={{
      user, userRoleIds, roles, childProfiles, posts, selectedChildProfile, selectedOrganisation,
      orgConfig, entitlements, activeUser, activeUserToken, idleTimeoutMs, isLocked,
      setUser, setUserRoleIds, setRoles, setChildProfiles, setPosts, setSelectedChildProfile,
      setSelectedOrganisation, setOrgConfig, setEntitlements, setActiveUser, setActiveUserToken, setIdleTimeoutMs,
      setIsLocked,
    }}>
      {children}
    </AppStateContext.Provider>
  );
};

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};
